// Zone spells — lifetimes, data-driven membership buffs, bombardment impacts (§23.1).

import { spellDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import type { BombardmentSpec } from '../../content/types';
import { dist, vec } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Zone } from '../state';

const dt = TUNING.dt;
const ZONE_SOURCE = 'zone-buff';

/** One bombardment impact: deterministic offset per remaining count, area damage. */
function bombardmentImpact(sim: Sim, z: Zone, spec: BombardmentSpec): void {
  const i = spec.impacts - (z.impactsRemaining ?? 0); // 0..N-1
  const angle = i * 2.4; // golden-ish spiral over the zone
  const r = z.radius * 0.65 * ((i % 3) / 2 + 0.25);
  const pos = vec(z.pos.x + Math.cos(angle) * r, z.pos.y + Math.sin(angle) * r);
  sim.pushEvent({ type: 'BarrageImpact', pos: { ...pos } });
  for (const s of [...sim.state.squads.values()]) {
    if (!sim.areHostile(s.owner, z.owner)) continue;
    if (dist(s.pos, pos) > spec.impactRadius) continue;
    const sd = unitDef(s.defId);
    const mult = sd.sizeTag === 'large' || sd.sizeTag === 'giant' ? spec.largeMult : 1;
    sim.damageSquad(s, spec.squadDamage * mult, { isRanged: true, attackerOwner: z.owner });
  }
  for (const w of [...sim.state.workers.values()]) {
    if (!sim.areHostile(w.owner, z.owner) || w.state === 'sheltered') continue;
    if (dist(w.pos, pos) <= spec.impactRadius) sim.damageWorker(w, spec.workerDamage);
  }
  for (const b of [...sim.state.buildings.values()]) {
    if (!sim.areHostile(b.owner, z.owner)) continue;
    if (dist(b.pos, pos) > spec.impactRadius + 2) continue;
    const dmg = b.isCore ? spec.buildingDamage * spec.coreDamageMult : spec.buildingDamage;
    sim.damageBuilding(b, dmg, { isSiege: true, attackerOwner: z.owner });
  }
  for (const m of [...sim.state.mines.values()]) {
    if (!sim.areHostile(m.owner, z.owner)) continue;
    if (dist(m.pos, pos) <= spec.impactRadius) sim.damageMine(m, spec.buildingDamage);
  }
}

/** Is pos inside the zone's lane rectangle (from zone.pos, along lane.dir)? */
function inLane(z: Zone, pos: { x: number; y: number }): boolean {
  if (!z.lane) return false;
  const dx = pos.x - z.pos.x;
  const dy = pos.y - z.pos.y;
  const along = dx * z.lane.dir.x + dy * z.lane.dir.y;
  if (along < -2 || along > z.lane.length) return false;
  const across = Math.abs(dx * -z.lane.dir.y + dy * z.lane.dir.x);
  return across <= z.lane.width / 2;
}

/** One-shot lane assault (Wild Stampede): knock aside infantry, slow Large, clear traps. */
function resolveLaneAssault(sim: Sim, z: Zone): void {
  const sd = spellDef(z.spellId);
  const spec = sd.laneAssault!;
  for (const s of [...sim.state.squads.values()]) {
    if (!inLane(z, s.pos)) continue;
    const d = unitDef(s.defId);
    if (sim.areHostile(s.owner, z.owner)) {
      if (d.sizeTag === 'small' || d.sizeTag === 'medium') {
        sim.damageSquad(s, spec.damage, { isRanged: false, attackerOwner: z.owner });
        if (!s.dead) {
          // knock perpendicular to the lane
          const side = ((s.id % 2) * 2 - 1) * spec.knockback;
          sim.teleportSquad(
            s,
            { x: s.pos.x + -z.lane!.dir.y * side, y: s.pos.y + z.lane!.dir.x * side },
            spec.knockback,
          );
        }
      } else if (d.sizeTag === 'large') {
        s.buffs.push({ stat: 'moveSpeed', mult: 0.6, remaining: spec.slowLargeSeconds });
      }
    } else if (sim.areAllies(s.owner, z.owner) && (d.roleTags.includes('boss') || d.sizeTag === 'large' || d.sizeTag === 'giant')) {
      s.buffs.push({ stat: 'moveSpeed', mult: 1.2, remaining: spec.speedBuffSeconds });
    }
  }
  // visible traps in the lane are swept away
  for (const m of [...sim.state.mines.values()]) {
    if (sim.areHostile(m.owner, z.owner) && inLane(z, m.pos)) sim.damageMine(m, 9999);
  }
  sim.pushEvent({ type: 'BarrageImpact', pos: { ...z.pos } });
}

export function stepSpellsAndZones(sim: Sim): void {
  const st = sim.state;

  // remove previous zone-sourced buffs, then re-apply for squads inside living zones
  for (const s of st.squads.values()) {
    for (let i = s.buffs.length - 1; i >= 0; i--) {
      const b = s.buffs[i] as { source?: string } & (typeof s.buffs)[number];
      if (b.source === ZONE_SOURCE) s.buffs.splice(i, 1);
    }
  }

  for (const z of [...st.zones.values()]) {
    z.remaining -= dt;
    if (z.remaining <= 0) {
      st.zones.delete(z.id);
      continue;
    }
    const sd = spellDef(z.spellId);
    if (sd.laneAssault && z.lane) {
      if ((z.telegraphRemaining ?? 0) > 0) {
        z.telegraphRemaining! -= dt;
      } else if (!z.laneResolved) {
        z.laneResolved = true;
        resolveLaneAssault(sim, z);
      }
      continue;
    }
    if (sd.bombardment) {
      if ((z.telegraphRemaining ?? 0) > 0) {
        z.telegraphRemaining! -= dt;
      } else if ((z.impactsRemaining ?? 0) > 0) {
        z.impactTimer = (z.impactTimer ?? 0) - dt;
        if (z.impactTimer <= 0) {
          z.impactTimer = sd.bombardment.impactInterval;
          bombardmentImpact(sim, z, sd.bombardment);
          z.impactsRemaining! -= 1;
        }
      }
      continue; // no membership buffs
    }
    const zb = sd.zoneBuffs;
    if (!zb) continue;
    for (const s of st.squads.values()) {
      const friendly = sim.areAllies(s.owner, z.owner);
      if (zb.appliesTo === 'friendly' ? !friendly : friendly) continue;
      const d = unitDef(s.defId);
      if (zb.excludeSizeTags?.includes(d.sizeTag)) continue;
      if (dist(s.pos, z.pos) > z.radius) continue;
      const isRanged = (d.attack?.range ?? 0) > 4;
      for (const entry of zb.entries) {
        if (entry.filter === 'ranged' && !isRanged) continue;
        if (entry.filter === 'melee' && isRanged) continue;
        s.buffs.push(
          Object.assign(
            { stat: entry.stat as (typeof s.buffs)[number]['stat'], mult: entry.mult, remaining: 0.11 },
            { source: ZONE_SOURCE },
          ),
        );
      }
    }
  }
}
